import {useMemo} from 'react';

/**
 * Minimal shape any primary-instance-aware record exposes. The backend computes
 * `isPrimaryInstance` relative to the requesting instance, so the locking rule is
 * purely client-side: a record owned by another (primary) instance is read-only here.
 */
export interface PrimaryInstanceOwned {
  isPrimaryInstance?: boolean;
  primaryInstanceName?: string;
}

export interface PrimaryInstanceLock {
  /** True when the record is owned by another instance (not the current/primary one). */
  isLocked: boolean;
  /** Mirror of the record flag (undefined until the record loads). */
  isPrimaryInstance?: boolean;
  /** Display name of the owning instance, with a safe fallback. */
  managedByName: string;
}

const DEFAULT_MANAGED_BY = 'the primary company';

/**
 * Centralizes the "owned by the primary instance" rule used to lock account,
 * identity and authentication areas of personnel records. Returns a structured
 * lock so callers don't re-derive `isPrimaryInstance === false` or the
 * "managed by" label inline.
 */
export function usePrimaryInstanceLock(
  item?: PrimaryInstanceOwned
): PrimaryInstanceLock {
  return useMemo(() => {
    const isLocked = item?.isPrimaryInstance === false;
    return {
      isLocked,
      isPrimaryInstance: item?.isPrimaryInstance,
      managedByName: item?.primaryInstanceName ?? DEFAULT_MANAGED_BY,
    };
  }, [item?.isPrimaryInstance, item?.primaryInstanceName]);
}
